import React from 'react';

import {Germline, useGermlinesQuery} from 'antibodies/germline/useGermlinesQuery';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

export interface GermlineSelectorProps {
  selectedGermlineId: string | null;
  onSelectGermline: (germlineId: string) => void;
  disabled: boolean;
  displayName?: string;
  extraOptions?: Array<OptionData>;
}

function toOptionDatas(germlines: Array<Germline>, extraOptions: Array<OptionData>): Array<OptionData> {
  const options: Array<OptionData> = germlines.map((germline) => ({
    name: germline.name,
    value: germline.id,
  }));
  return options.concat(extraOptions);
}

export default function GermlineSelector(props: GermlineSelectorProps): JSX.Element {
  const {status, germlines} = useGermlinesQuery();

  switch (status) {
    case QueryResultType.PENDING:
      return <CenteredSpinner />;
    case QueryResultType.ERROR:
      return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load germlines." />;
    case QueryResultType.SUCCESS: {
      const selectedGermline =
        props.selectedGermlineId != null ? germlines.find((g) => g.id === props.selectedGermlineId) : null;
      const displayName = props.displayName ?? selectedGermline?.name ?? 'Select germline';
      return (
        <SelectDropdown
          disabled={props.disabled}
          displayName={displayName}
          menu={
            <SearchableMenu
              width={Menu.WIDTHS.FULL}
              onSelect={(value: OptionValue) => {
                props.onSelectGermline(value as string);
              }}
              optionDatas={toOptionDatas(germlines, props.extraOptions ?? [])}
              selectedValue={props.selectedGermlineId}
            />
          }
        />
      );
    }
    default:
      return assertNever(status);
  }
}
